/**
 * Inventory page - MarketCheck-driven dealer inventory browser. V1
 * parity rewrite (#135) - stat strip, grid/table view toggle, polished
 * card layout with hero image + Used/New badge, vendor-listing click-
 * through.
 *
 * Data flow: GET /pos/v1/locations/{id}/inventory dispatches to
 * MarketCheck via the V0060 binding cascade when bound; otherwise
 * falls back to V0052 vehicle_inventory_tbl. Adapter responses carry
 * image URLs, inventory_type ('new' | 'used'), and vdp_url; DB-fallback
 * rows return None for those fields and the cards degrade cleanly
 * (placeholder image, no badge, image not clickable).
 *
 * Take Deposit CTA pre-fills the take-payment wizard:
 *   * stock_number → reference
 *   * (listed_price × down_payment_percentage) → totalCents
 *   * department → "sales" (overrides cashier's per-membership default)
 *   * description → V1-format multi-line vehicle blurb
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Car,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ExternalLink,
  Gift,
  History,
  LayoutGrid,
  Receipt as ReceiptIcon,
  RefreshCw,
  Rows3,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError, type InventoryItem, type InventoryStatus, type Profile } from "@tilt/pos-sdk";

import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { TextInput } from "../components/ui/Field";
import { InlineSpinner, PageSpinner } from "../components/ui/Spinner";
import { getPosClient } from "../lib/api";
import { FEATURE_INVENTORY, isFeatureEnabled } from "../lib/features";
import { formatCents } from "../lib/format";
import { usePageTitle } from "../lib/use-page-title";
import { cn } from "../lib/utils";
import { POS_ORDER_CREATE } from "../lib/permissions";

const PAGE_SIZE = 25;

const STATUS_OPTIONS = ["all", "available", "on_hold", "sold", "service", "retired"] as const;

type ViewMode = "grid" | "table";

export function InventoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  usePageTitle("inventory");

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
  });

  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_OPTIONS)[number]>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  // V1 parity - view toggle. Defaults to grid (V1's default). Local
  // only; resets to grid on remount. Persistence is a follow-up if it
  // becomes a workflow ask.
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
      setPage(0);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, debouncedSearch]);

  const locationId = profile.data ? pickInventoryLocationId(profile.data) : null;

  // V0070 - server returns the FULL inventory snapshot per request,
  // cached per (location, provider) on S3 in the location's local-day
  // bucket. Filter / sort / pagination all happen client-side from
  // here on. ``staleTime: Infinity`` because the server's freshness
  // window is its own concern (rolls over at location-local midnight,
  // or on the explicit Refresh CTA below).
  const inventoryKey = useMemo(
    () => ["inventory", locationId, statusFilter !== "all" ? statusFilter : null] as const,
    [locationId, statusFilter],
  );
  const inventory = useQuery({
    enabled: locationId != null,
    queryKey: inventoryKey,
    queryFn: () =>
      getPosClient().listLocationInventory(locationId!, {
        ...(statusFilter !== "all" ? { status: statusFilter as InventoryStatus } : {}),
      }),
    placeholderData: (prev) => prev,
    staleTime: Infinity,
  });

  // Refresh CTA - POST /inventory/refresh always re-pulls upstream.
  // On success we replace the cache entry in-place so React Query
  // doesn't have to round-trip again.
  const refresh = useMutation({
    mutationFn: () => getPosClient().refreshLocationInventory(locationId!),
    onSuccess: (data) => {
      qc.setQueryData(inventoryKey, data);
    },
  });

  // Client-side filter - search across stock # / VIN / make / model /
  // trim. The backend's V0070 cache is the FULL snapshot; doing this
  // here keeps the UI responsive between refreshes without round-trips.
  // Hooks must run unconditionally so this memo + the data-derived
  // locals below sit ABOVE the early-return permission/feature gates.
  const allItems = inventory.data?.items;
  const filtered = useMemo(() => {
    const list = allItems ?? [];
    if (!debouncedSearch) return list;
    return list.filter((item) => matchesSearch(item, debouncedSearch));
  }, [allItems, debouncedSearch]);

  if (profile.isPending) {
    return <PageSpinner label={t("common.loading")} />;
  }
  if (profile.isError || !profile.data) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="error" title={t("shell.loadFailed")}>
          {t("shell.tryAgain")}
        </Alert>
      </div>
    );
  }

  const perms = new Set(profile.data.permissions);
  if (!perms.has(POS_ORDER_CREATE)) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="warn" title={t("inventory.noPermissionTitle")}>
          {t("inventory.noPermissionBody")}
        </Alert>
      </div>
    );
  }

  // Feature gate - admin can disable Vehicle inventory at any cascade
  // scope (partner / org / location). The sidebar already hides the
  // tab when it's off, but a direct URL hit (bookmark, deep link,
  // back button) would reach this page; gate the body so the cashier
  // sees a clean "feature off here" notice instead of an empty grid
  // or a 403 from the inventory fetch. (#226)
  const isLocationScope = profile.data.active_membership?.scope_kind === "location";
  if (isLocationScope && !isFeatureEnabled(profile.data.features, FEATURE_INVENTORY)) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="warn" title={t("inventory.featureDisabledTitle")}>
          {t("inventory.featureDisabledBody")}
        </Alert>
      </div>
    );
  }

  if (locationId == null) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="warn" title={t("inventory.noLocationsTitle")}>
          {t("inventory.noLocationsBody")}
        </Alert>
      </div>
    );
  }

  const downPaymentPct = clampPct(inventory.data?.down_payment_percentage);
  // Adapter slug - "marketcheck" / "db" / etc. Used to hide the
  // status filter when the source doesn't carry status (adapter
  // responses are status-less).
  const provider = inventory.data?.provider ?? "db";
  const isAdapterServed = provider !== "db";
  // V0070 - server tells us when the snapshot was last refreshed
  // upstream. Drives the "Last refreshed N min ago" indicator and
  // disables the Refresh CTA on already-fresh state.
  const fetchedAtIso = inventory.data?.fetched_at ?? null;
  const source = inventory.data?.source ?? "db";
  const totalCatalogue = (allItems ?? []).length;

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const offset = safePage * PAGE_SIZE;
  const items = filtered.slice(offset, offset + PAGE_SIZE);
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + items.length, total);
  const hasPrev = safePage > 0;
  const hasNext = safePage < totalPages - 1;

  // CTA pre-fills the take-payment wizard so the cashier doesn't retype
  // anything. Vehicle take-deposits always land on the Sales department,
  // regardless of the cashier's per-membership default.
  const onTakePayment = (item: InventoryItem) => {
    const refParts = [
      item.stock_number,
      item.year ? String(item.year) : null,
      item.make,
      item.model,
    ]
      .filter((s): s is string => !!s && s.length > 0)
      .join(" ");
    navigate("/sale", {
      state: {
        initialDepartmentName: "sales",
        initialReference: item.stock_number ?? refParts,
        initialTotalCents: computeDownPaymentCents(item.listed_price_cents, downPaymentPct),
        initialDescription: composeVehicleDescription(item),
      },
    });
  };

  return (
    // Inventory is a card-grid page; on a wide screen it can fit 4-5
    // cars across instead of 3 if we drop the 1280 cap. (2026-05-03
    // dogfood - "inventory may be too" should take more width.)
    <div className="space-y-6 pb-12">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted">
          {t("inventory.kicker")}
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-strong sm:text-3xl xl:text-4xl">
          {t("inventory.title")}
        </h1>
        <p className="mt-1 text-sm text-muted">{t("inventory.subtitle")}</p>
      </header>

      {/* Stat strip - Total / New / Used / Average. V1 parity layout
          with gradient cards. Computed from the FULL snapshot, not
          the current filtered page, so the counters reflect the
          dealer's whole inventory regardless of the cashier's search
          / status selection. Hidden on the empty-state to keep the
          page calm when there's nothing to count. */}
      {totalCatalogue > 0 && allItems && <StatStrip items={allItems} />}

      {/* Filter strip - search + status + view toggle + inline spinner.
          Status filter is suppressed when an external adapter is the
          source (V0058 - adapter responses don't carry status). */}
      <div className="flex flex-col gap-3 rounded-xl bg-surface p-4 shadow-elev-1 sm:flex-row sm:items-center sm:gap-3 sm:p-5">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
            aria-hidden="true"
          />
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("inventory.searchPlaceholder")}
            className="min-h-tap w-full pl-9 sm:w-72"
          />
        </div>
        {!isAdapterServed && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            aria-label={t("inventory.filterByStatus")}
            className="min-h-tap appearance-none rounded-md bg-surface-muted px-3 text-sm font-medium text-foreground ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-brand-primary sm:w-44"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(statusLabelKey(s))}
              </option>
            ))}
          </select>
        )}
        {(inventory.isFetching || refresh.isPending) && (
          <span className="hidden items-center gap-2 text-xs text-muted sm:inline-flex">
            <InlineSpinner /> {t("common.loading")}
          </span>
        )}
        {/* Right-side controls cluster: optional last-refreshed
            timestamp (desktop only), refresh icon-button (V0070),
            view toggle. Lives in one ``ml-auto`` flex row so the
            cluster reads as a single controls group on every
            breakpoint instead of wrapping unpredictably on mobile. */}
        <div className="flex items-center justify-end gap-2 sm:ml-auto">
          {isAdapterServed && fetchedAtIso && (
            <span className="hidden text-[0.6875rem] text-muted sm:inline" title={fetchedAtIso}>
              {t("inventory.lastRefreshed", {
                relative: relativeFromNow(fetchedAtIso, t),
              })}
              {source === "live" ? ` · ${t("inventory.sourceLive")}` : ""}
            </span>
          )}
          {/* Refresh - V0070. Deliberately a small icon (not a labelled
              CTA) because every press re-pulls upstream and we don't
              want cashiers stress-tapping it. The relative-refreshed-at
              + source live in the title tooltip so the affordance is
              still discoverable. Also surfaced on the error path so a
              5xx can be retried without a hard reload - when the GET
              fails ``inventory.data`` is undefined and ``provider``
              defaults to ``"db"``, which would otherwise hide the
              icon at the exact moment a retry is most useful. Stays
              hidden only on the genuine DB-fallback success path
              (data loaded, ``provider === "db"``). */}
          {(isAdapterServed || inventory.isError) && (
            <button
              type="button"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending || locationId == null}
              aria-label={t("inventory.refresh")}
              title={
                fetchedAtIso
                  ? `${t("inventory.lastRefreshed", {
                      relative: relativeFromNow(fetchedAtIso, t),
                    })}${source === "live" ? ` · ${t("inventory.sourceLive")}` : ""}`
                  : t("inventory.refresh")
              }
              className="flex h-9 w-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw
                className={cn("h-4 w-4", refresh.isPending && "animate-spin")}
                aria-hidden="true"
              />
            </button>
          )}
          {/* View toggle - segmented control. Subtle (no full-fill
              purple) so it doesn't compete with the page's primary
              CTA (Take Deposit). */}
          <div
            className="inline-flex rounded-lg bg-surface-muted p-1"
            role="group"
            aria-label={t("inventory.viewToggleLabel")}
          >
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              aria-pressed={viewMode === "grid"}
              title={t("inventory.gridView")}
              className={cn(
                "flex h-9 w-10 items-center justify-center rounded-md text-sm transition-colors",
                viewMode === "grid"
                  ? "bg-surface text-strong shadow-elev-1"
                  : "text-muted hover:text-foreground",
              )}
            >
              <LayoutGrid className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              aria-pressed={viewMode === "table"}
              title={t("inventory.tableView")}
              className={cn(
                "flex h-9 w-10 items-center justify-center rounded-md text-sm transition-colors",
                viewMode === "table"
                  ? "bg-surface text-strong shadow-elev-1"
                  : "text-muted hover:text-foreground",
              )}
            >
              <Rows3 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Data surface - grid (default) or table view. */}
      <section
        className={cn(
          "overflow-hidden rounded-xl",
          viewMode === "table" ? "bg-surface shadow-elev-1" : "",
        )}
        aria-label={t("inventory.title")}
      >
        {inventory.isError ? (
          <div className="px-5 py-5 sm:px-6">
            {(() => {
              // Provider misconfigured - surface the vendor-side
              // detail (e.g. "MarketCheck binding requires source_id")
              // so the cashier knows what to ask their admin to fix
              // instead of seeing a generic "couldn't load".
              const err = inventory.error;
              if (
                err instanceof ApiError &&
                err.status === 422 &&
                isBindingMisconfigured(err.body)
              ) {
                const detail = (err.body as { detail?: string }).detail ?? t("inventory.loadError");
                return (
                  <Alert tone="warn" title={t("inventory.providerMisconfiguredTitle")}>
                    {detail}
                  </Alert>
                );
              }
              return (
                <Alert tone="error" title={t("inventory.loadError")}>
                  {String((err as Error)?.message ?? "")}
                </Alert>
              );
            })()}
          </div>
        ) : inventory.isPending ? (
          <div className="px-5 py-12 sm:px-6">
            <PageSpinner label={t("common.loading")} />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl bg-surface px-5 py-8 shadow-elev-1 sm:px-6">
            <EmptyState
              icon={<Car className="h-8 w-8" />}
              title={t("inventory.emptyTitle")}
              body={t("inventory.emptyBody")}
            />
          </div>
        ) : viewMode === "grid" ? (
          <VehicleGrid
            items={items}
            downPaymentPct={downPaymentPct}
            onTakePayment={onTakePayment}
          />
        ) : (
          <VehicleTable
            items={items}
            downPaymentPct={downPaymentPct}
            onTakePayment={onTakePayment}
            isAdapterServed={isAdapterServed}
          />
        )}

        {items.length > 0 && (
          <div
            className={cn(
              "flex items-center justify-between gap-3 px-5 py-3 text-xs text-muted sm:px-6",
              viewMode === "table"
                ? "bg-surface-muted"
                : "mt-4 rounded-xl bg-surface shadow-elev-1",
            )}
          >
            <span className="font-mono tabular-nums">
              {t("inventory.pager", { start, end, total })}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                disabled={!hasPrev}
                onClick={() => setPage(Math.max(0, safePage - 1))}
                leftIcon={<ChevronLeft className="h-4 w-4" />}
                className="min-h-tap"
              >
                {t("inventory.prev")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={!hasNext}
                onClick={() => setPage(safePage + 1)}
                rightIcon={<ChevronRight className="h-4 w-4" />}
                className="min-h-tap"
              >
                {t("inventory.next")}
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- //
// Stat strip                                                       //
// ---------------------------------------------------------------- //

function StatStrip(props: { readonly items: readonly InventoryItem[] }) {
  const { t } = useTranslation();
  const { items } = props;
  const stats = useMemo(() => {
    let newCount = 0;
    let usedCount = 0;
    let priceSum = 0;
    let priceCount = 0;
    for (const item of items) {
      if (item.inventory_type === "new") newCount += 1;
      else if (item.inventory_type === "used") usedCount += 1;
      if (item.listed_price_cents != null && item.listed_price_cents > 0) {
        priceSum += item.listed_price_cents;
        priceCount += 1;
      }
    }
    const averageCents = priceCount > 0 ? Math.round(priceSum / priceCount) : 0;
    return {
      total: items.length,
      new: newCount,
      used: usedCount,
      averageCents,
    };
  }, [items]);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
      <StatCard
        label={t("inventory.statTotal")}
        value={stats.total.toLocaleString()}
        icon={<Car className="h-5 w-5" />}
        gradient="from-slate-700 to-cyan-600"
      />
      <StatCard
        label={t("inventory.statNew")}
        value={stats.new.toLocaleString()}
        icon={<Gift className="h-5 w-5" />}
        gradient="from-emerald-700 to-emerald-400"
      />
      <StatCard
        label={t("inventory.statUsed")}
        value={stats.used.toLocaleString()}
        icon={<History className="h-5 w-5" />}
        gradient="from-orange-600 to-amber-500"
      />
      <StatCard
        label={t("inventory.statAverage")}
        value={stats.averageCents > 0 ? formatWholeDollars(stats.averageCents) : "-"}
        icon={<CircleDollarSign className="h-5 w-5" />}
        gradient="from-violet-700 to-indigo-500"
      />
    </div>
  );
}

function StatCard(props: {
  readonly label: string;
  readonly value: string;
  readonly icon: React.ReactNode;
  readonly gradient: string;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2 rounded-xl bg-gradient-to-br p-3 text-white shadow-elev-1 sm:gap-3 sm:p-4 lg:gap-4 lg:p-5",
        props.gradient,
      )}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm sm:h-10 sm:w-10 lg:h-12 lg:w-12">
        {props.icon}
      </div>
      <div className="min-w-0 flex-1">
        {/* Responsive sizing - value text scales down on tight tablet
            widths so currency strings like "$63,249" don't truncate. */}
        <div
          className="truncate text-base font-bold tabular-nums sm:text-lg lg:text-2xl"
          title={props.value}
        >
          {props.value}
        </div>
        <div className="truncate text-[0.625rem] font-medium opacity-90 sm:text-xs lg:text-sm">
          {props.label}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- //
// Grid view - V1-style vehicle cards                               //
// ---------------------------------------------------------------- //

function VehicleGrid(props: {
  readonly items: readonly InventoryItem[];
  readonly downPaymentPct: number;
  readonly onTakePayment: (item: InventoryItem) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      {props.items.map((item) => (
        <VehicleCard
          key={item.id}
          item={item}
          downPaymentPct={props.downPaymentPct}
          onTakePayment={() => props.onTakePayment(item)}
        />
      ))}
    </div>
  );
}

function VehicleCard(props: {
  readonly item: InventoryItem;
  readonly downPaymentPct: number;
  readonly onTakePayment: () => void;
}) {
  const { t } = useTranslation();
  const { item, downPaymentPct, onTakePayment } = props;
  const heroUrl = item.image_urls?.[0] ?? null;
  const downCents = computeDownPaymentCents(item.listed_price_cents, downPaymentPct);
  const titleStr = vehicleLabel(item, t);
  const fullTitle = item.trim ? `${titleStr} ${item.trim}` : titleStr;
  return (
    <div className="group flex flex-col overflow-hidden rounded-xl bg-surface shadow-elev-1 transition-all hover:-translate-y-0.5 hover:shadow-elev-2">
      {/* Hero - clickable when vdp_url present, opens vendor listing. */}
      <VehicleHero
        heroUrl={heroUrl}
        vdpUrl={item.vdp_url ?? null}
        inventoryType={item.inventory_type ?? null}
        alt={fullTitle}
      />

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <h3
            className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-strong"
            title={fullTitle}
          >
            {fullTitle}
          </h3>
          {item.listed_price_cents != null ? (
            <div className="mt-2 inline-flex items-center rounded-full bg-brand-primary px-3 py-1 text-xs font-semibold tabular-nums text-brand-primary-fg">
              {formatCents(item.listed_price_cents, "USD")}
            </div>
          ) : (
            <div className="mt-2 text-xs font-medium text-muted">{t("inventory.callForPrice")}</div>
          )}
          {downCents != null && (
            <div className="mt-1 text-[0.6875rem] text-muted">
              {t("inventory.downPaymentHint", {
                pct: downPaymentPct,
                amount: formatCents(downCents, "USD"),
              })}
            </div>
          )}
        </div>

        {/* 2x2 meta grid - Mileage / Exterior / Stock # / Interior */}
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          <MetaItem
            label={t("inventory.metaMileage")}
            value={item.mileage != null ? `${item.mileage.toLocaleString()} mi` : "-"}
            mono
          />
          <MetaItem label={t("inventory.metaExterior")} value={item.exterior_color ?? "-"} />
          <MetaItem label={t("inventory.metaStock")} value={item.stock_number ?? "-"} mono />
          <MetaItem label={t("inventory.metaInterior")} value={item.interior_color ?? "-"} />
        </dl>

        <div className="mt-auto pt-2">
          <Button
            variant="primary"
            size="md"
            leftIcon={<ReceiptIcon className="h-4 w-4" />}
            onClick={onTakePayment}
            className="min-h-tap w-full"
          >
            {t("inventory.takePayment")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function VehicleHero(props: {
  readonly heroUrl: string | null;
  readonly vdpUrl: string | null;
  readonly inventoryType: "new" | "used" | null;
  readonly alt: string;
}) {
  const { t } = useTranslation();
  const { heroUrl, vdpUrl, inventoryType, alt } = props;
  const inner = (
    <div className="relative h-40 w-full overflow-hidden bg-surface-muted">
      {heroUrl ? (
        <img
          src={heroUrl}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          onError={(e) => {
            // Vendor URL 404'd or was blocked - drop to the placeholder
            // so the card doesn't render a broken-image icon.
            const el = e.currentTarget as HTMLImageElement;
            el.style.display = "none";
            const sibling = el.parentElement?.querySelector(
              "[data-fallback]",
            ) as HTMLElement | null;
            if (sibling) sibling.style.display = "flex";
          }}
        />
      ) : null}
      <div
        data-fallback
        className={cn(
          "absolute inset-0 flex items-center justify-center bg-surface-muted text-muted",
          heroUrl ? "hidden" : "flex",
        )}
      >
        <Car className="h-10 w-10" aria-hidden="true" />
      </div>
      {inventoryType && (
        <span
          className={cn(
            "absolute left-2 top-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider shadow-sm",
            // Darker shades pass WCAG AA (4.5:1) against white text;
            // amber-500 + white was 2.14:1 (axe finding). emerald-700
            // is 5.43:1, amber-700 is 4.73:1.
            inventoryType === "new" ? "bg-emerald-700 text-white" : "bg-amber-700 text-white",
          )}
        >
          {inventoryType === "new" ? t("inventory.typeNew") : t("inventory.typeUsed")}
        </span>
      )}
      {vdpUrl && (
        <div className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-strong opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </div>
      )}
    </div>
  );
  if (vdpUrl) {
    return (
      <a
        href={vdpUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={t("inventory.viewListing")}
        className="block focus:outline-none focus:ring-2 focus:ring-brand-primary"
      >
        {inner}
      </a>
    );
  }
  return inner;
}

function MetaItem(props: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[0.625rem] font-medium uppercase tracking-wider text-faint">
        {props.label}
      </dt>
      <dd
        className={cn("truncate text-xs font-medium text-foreground", props.mono && "font-mono")}
        title={props.value}
      >
        {props.value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------- //
// Table view - V1-style enriched table                             //
// ---------------------------------------------------------------- //

function VehicleTable(props: {
  readonly items: readonly InventoryItem[];
  readonly downPaymentPct: number;
  readonly onTakePayment: (item: InventoryItem) => void;
  readonly isAdapterServed: boolean;
}) {
  const { t } = useTranslation();
  const { items, downPaymentPct, onTakePayment, isAdapterServed } = props;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-surface-muted text-left text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
          <tr>
            <th scope="col" className="px-3 py-3 sm:px-4">
              {t("inventory.colImage")}
            </th>
            <th scope="col" className="px-3 py-3">
              {t("inventory.colStock")}
            </th>
            <th scope="col" className="px-3 py-3">
              {t("inventory.colVehicle")}
            </th>
            <th scope="col" className="px-3 py-3">
              {t("inventory.colType")}
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              {t("inventory.colPrice")}
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              {t("inventory.colMileage")}
            </th>
            <th scope="col" className="px-3 py-3">
              {t("inventory.colExterior")}
            </th>
            <th scope="col" className="px-3 py-3">
              {t("inventory.colInterior")}
            </th>
            {!isAdapterServed && (
              <th scope="col" className="px-3 py-3">
                {t("inventory.colStatus")}
              </th>
            )}
            <th scope="col" className="px-3 py-3 sm:px-4"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line/60">
          {items.map((item, i) => (
            <VehicleTableRow
              key={item.id}
              item={item}
              downPaymentPct={downPaymentPct}
              onTakePayment={() => onTakePayment(item)}
              isAdapterServed={isAdapterServed}
              striped={i % 2 === 1}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VehicleTableRow(props: {
  readonly item: InventoryItem;
  readonly downPaymentPct: number;
  readonly onTakePayment: () => void;
  readonly isAdapterServed: boolean;
  readonly striped: boolean;
}) {
  const { t } = useTranslation();
  const { item, downPaymentPct, onTakePayment, isAdapterServed, striped } = props;
  const heroUrl = item.image_urls?.[0] ?? null;
  const titleStr = vehicleLabel(item, t);
  return (
    <tr
      className={cn(
        "transition-colors",
        striped && "bg-surface-muted/40",
        "hover:bg-surface-muted",
      )}
    >
      <td className="px-3 py-2 align-middle sm:px-4">
        <VehicleThumb heroUrl={heroUrl} vdpUrl={item.vdp_url ?? null} alt={titleStr} />
      </td>
      <td className="px-3 py-3 align-top font-mono text-xs text-strong">
        {item.stock_number ?? "-"}
      </td>
      <td className="px-3 py-3 align-top">
        <div className="font-medium text-strong">{titleStr}</div>
        {item.trim && <div className="text-xs text-muted">{item.trim}</div>}
        {item.vin && (
          <div className="mt-0.5 font-mono text-[0.625rem] text-faint" title={item.vin}>
            {item.vin}
          </div>
        )}
      </td>
      <td className="px-3 py-3 align-top">
        {item.inventory_type ? (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider",
              item.inventory_type === "new"
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
            )}
          >
            {item.inventory_type === "new" ? t("inventory.typeNew") : t("inventory.typeUsed")}
          </span>
        ) : (
          <span className="text-faint">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right align-top font-mono font-semibold tabular-nums text-strong">
        {item.listed_price_cents != null ? (
          <>
            {formatCents(item.listed_price_cents, "USD")}
            {(() => {
              const dc = computeDownPaymentCents(item.listed_price_cents, downPaymentPct);
              return dc != null ? (
                <div className="text-[0.6875rem] font-normal text-muted">
                  {t("inventory.downPaymentHint", {
                    pct: downPaymentPct,
                    amount: formatCents(dc, "USD"),
                  })}
                </div>
              ) : null;
            })()}
          </>
        ) : (
          "-"
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right align-top font-mono tabular-nums text-foreground">
        {item.mileage != null ? `${item.mileage.toLocaleString()} mi` : "-"}
      </td>
      <td className="px-3 py-3 align-top text-foreground">{item.exterior_color ?? "-"}</td>
      <td className="px-3 py-3 align-top text-foreground">{item.interior_color ?? "-"}</td>
      {!isAdapterServed && (
        <td className="px-3 py-3 align-top">
          <span className="inline-block rounded-md bg-surface-muted px-2 py-0.5 text-xs font-medium text-foreground">
            {t(statusLabelKey(item.status))}
          </span>
        </td>
      )}
      <td className="px-3 py-3 align-top sm:px-4">
        <Button
          size="sm"
          variant="primary"
          leftIcon={<ReceiptIcon className="h-4 w-4" />}
          onClick={onTakePayment}
          className="min-h-tap"
        >
          {t("inventory.takePayment")}
        </Button>
      </td>
    </tr>
  );
}

function VehicleThumb(props: {
  readonly heroUrl: string | null;
  readonly vdpUrl: string | null;
  readonly alt: string;
}) {
  const { heroUrl, vdpUrl, alt } = props;
  const inner = (
    <div className="h-12 w-20 overflow-hidden rounded-md bg-surface-muted shadow-sm">
      {heroUrl ? (
        <img
          src={heroUrl}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
          }}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-faint">
          <Car className="h-5 w-5" aria-hidden="true" />
        </div>
      )}
    </div>
  );
  if (vdpUrl) {
    return (
      <a
        href={vdpUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block focus:outline-none focus:ring-2 focus:ring-brand-primary"
      >
        {inner}
      </a>
    );
  }
  return inner;
}

// ---------------------------------------------------------------- //
// Helpers                                                           //
// ---------------------------------------------------------------- //

function vehicleLabel(item: InventoryItem, t: (k: string) => string): string {
  const parts = [item.year ? String(item.year) : null, item.make, item.model].filter(
    (s): s is string => !!s && s.length > 0,
  );
  if (parts.length === 0) return t("inventory.unknownVehicle");
  return parts.join(" ");
}

/** Clamp the configurable down-payment % to [1, 100]. Defaults to 20
 *  when undefined / out of range - same default the backend applies
 *  when no scope sets ``vehicle_down_payment_percentage``. */
function clampPct(raw: number | undefined): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 20;
  return Math.max(1, Math.min(100, Math.round(raw)));
}

/** Compute the down-payment in cents from a listed price + percentage.
 *  Returns ``null`` when the listed price is missing - caller hides the
 *  hint and falls back to the standard listed-price flow. */
function computeDownPaymentCents(listedPriceCents: number | null, pct: number): number | null {
  if (listedPriceCents == null) return null;
  return Math.round((listedPriceCents * pct) / 100);
}

/** Format integer cents as whole dollars - no cents column. Used by the
 *  stat-strip Average Price tile, where "$63,249" reads cleaner than
 *  "$63,248.64". formatCents elsewhere keeps two decimals because
 *  individual sale amounts need cent precision. */
function formatWholeDollars(cents: number): string {
  const dollars = Math.round(cents / 100);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(dollars);
  } catch {
    return `$${dollars.toLocaleString()}`;
  }
}

/** Build the description string the take-payment wizard pre-fills in
 *  the order's description field. V1-format multi-line:
 *
 *      2025 Genesis G90 Base
 *      Color: Blue exterior, Urban Brown/Glacier White interior
 *      VIN: KMTFC4SD2SU047083
 *      Engine: 3.5L V6
 *      Miles: 8,426
 *
 *  Lines are skipped when their underlying data is missing - a vehicle
 *  with no engine (or the DB-fallback path that doesn't carry engine)
 *  drops the Engine line entirely instead of writing "Engine: N/A". */
function composeVehicleDescription(item: InventoryItem): string {
  const lines: string[] = [];
  const head = [item.year ? String(item.year) : null, item.make, item.model, item.trim]
    .filter((s): s is string => !!s && s.length > 0)
    .join(" ");
  if (head) lines.push(head);
  if (item.exterior_color || item.interior_color) {
    const ext = item.exterior_color?.trim() || "-";
    const int = item.interior_color?.trim() || "-";
    lines.push(`Color: ${ext} exterior, ${int} interior`);
  }
  if (item.vin) lines.push(`VIN: ${item.vin}`);
  if (item.engine?.trim()) lines.push(`Engine: ${item.engine.trim()}`);
  if (item.mileage != null) lines.push(`Miles: ${item.mileage.toLocaleString()}`);
  return lines.join("\n");
}

function statusLabelKey(status: string): string {
  switch (status) {
    case "all":
      return "inventory.statusAll";
    case "available":
      return "inventory.statusAvailable";
    case "on_hold":
      return "inventory.statusOnHold";
    case "sold":
      return "inventory.statusSold";
    case "service":
      return "inventory.statusService";
    case "retired":
      return "inventory.statusRetired";
    default:
      return status;
  }
}

/** Narrow the ApiError body shape for the 422 "binding_misconfigured"
 *  response so we can render the vendor-side ``detail`` cleanly
 *  without a runtime cast at every call site. */
function isBindingMisconfigured(body: unknown): body is { error: string; detail?: string } {
  return (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    (body as { error: unknown }).error === "binding_misconfigured"
  );
}

/** Pick a single ``location_id`` to fetch inventory for, **constrained
 *  to the active scope's subtree**.
 *
 *  Rules:
 *    1. Active scope is a specific location → use that location_id.
 *    2. Active scope is an organization → first location-scoped
 *       membership whose ``organization.id`` matches active.org.
 *    3. Active scope is a partner → first location-scoped membership
 *       whose ``partner.id`` matches active.partner.
 *    4. Nothing matches → null. The page renders a "no location
 *       available in this scope" notice rather than silently falling
 *       through to a location in some other org/partner.
 *
 *  The naive earlier version picked ``profile.memberships[0]``
 *  whenever the active scope wasn't a specific location - that
 *  would route a partner-admin's request through a location in a
 *  different partner subtree and the API's scope check 403'd
 *  ("location not in caller's scope"). Scoping the candidate set to
 *  the active subtree fixes that. */
function pickInventoryLocationId(profile: Profile): string | null {
  const active = profile.active_membership;
  if (!active) return null;

  if (active.scope_kind === "location") {
    return active.location?.id ?? null;
  }

  const candidateMemberships = profile.memberships.filter(
    (m) => m.scope_kind === "location" && m.location?.id,
  );

  if (active.scope_kind === "organization" && active.organization?.id) {
    const orgId = active.organization.id;
    const match = candidateMemberships.find((m) => m.organization?.id === orgId);
    return match?.location?.id ?? null;
  }

  if (active.scope_kind === "partner" && active.partner?.id) {
    const partnerId = active.partner.id;
    const match = candidateMemberships.find((m) => m.partner?.id === partnerId);
    return match?.location?.id ?? null;
  }

  return null;
}

/** Free-text match used to filter the cached snapshot client-side.
 *  Matches the same fields the V0052 trigram index covered server-side
 *  (stock # / VIN / make / model / trim) plus year so a cashier can
 *  type "2025 Accord" and find the row. ``q`` is already lowercased
 *  by the caller. */
function matchesSearch(item: InventoryItem, q: string): boolean {
  const haystack = [
    item.stock_number,
    item.vin,
    item.make,
    item.model,
    item.trim,
    item.year != null ? String(item.year) : null,
  ]
    .filter((s): s is string => !!s && s.length > 0)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

/** "5 min ago" / "2 h ago" / "yesterday" - relative time string for
 *  the "Last refreshed" indicator next to the Refresh CTA. Resolves
 *  i18n keys so the Spanish / French builds stay localised. ``t`` is
 *  the raw ``useTranslation()`` return; typed loosely here because
 *  i18next's TFunction generic doesn't compose cleanly with our
 *  ``exactOptionalPropertyTypes`` tsconfig. */
type TFn = ReturnType<typeof useTranslation>["t"];
function relativeFromNow(iso: string, t: TFn): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const ms = Math.max(0, Date.now() - then);
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return t("inventory.refreshedJustNow");
  if (minutes < 60) return t("inventory.refreshedMinutesAgo", { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t("inventory.refreshedHoursAgo", { count: hours });
  const days = Math.round(hours / 24);
  return t("inventory.refreshedDaysAgo", { count: days });
}
